import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Typography,
  Badge,
} from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { IconLock, IconChevronDown, IconChevronRight } from "@tabler/icons-react";
import { formatFilterExpression } from "./utils";
import type { Policy } from "../../services/rbac";

interface ClickHousePoliciesSectionProps {
  database: string;
  policiesByDatabase: Record<string, Record<string, Policy[]>>;
  expandedRows: Set<string>;
  toggleRow: (rowKey: string) => void;
}

export const ClickHousePoliciesSection = ({
  database,
  policiesByDatabase,
  expandedRows,
  toggleRow,
}: ClickHousePoliciesSectionProps) => {
  return (
    <Card>
      <CardHeader>
        <Stack direction="row" align="center" gap="sm">
          <IconLock className="al-w-5 al-h-5 al-text-blue-600" />
          <CardTitle>ClickHouse Policies - {database.toUpperCase()}</CardTitle>
        </Stack>
        <CardDescription>Row-level security policies for {database} database</CardDescription>
      </CardHeader>
      <CardContent>
        {!policiesByDatabase[database] || Object.keys(policiesByDatabase[database]).length === 0 ? (
          <Typography variant="body" className="al-text-gray-500">
            No policies configured for this database
          </Typography>
        ) : (
          <div className="al-border al-border-gray-200 al-rounded-lg al-overflow-hidden">
            <table className="al-w-full al-border-collapse">
              <thead className="al-bg-gray-50">
                <tr>
                  <th className="al-px-4 al-py-3 al-text-left al-text-xs al-font-medium al-text-gray-700 al-uppercase al-tracking-wider al-w-[60px]">
                    #
                  </th>
                  <th className="al-px-4 al-py-3 al-text-left al-text-xs al-font-medium al-text-gray-700 al-uppercase al-tracking-wider al-w-[200px]">
                    Table Name
                  </th>
                  <th className="al-px-4 al-py-3 al-text-left al-text-xs al-font-medium al-text-gray-700 al-uppercase al-tracking-wider">
                    Policy Name
                  </th>
                  <th className="al-px-4 al-py-3 al-text-left al-text-xs al-font-medium al-text-gray-700 al-uppercase al-tracking-wider al-w-[120px]">
                    Type
                  </th>
                  <th className="al-px-4 al-py-3 al-text-left al-text-xs al-font-medium al-text-gray-700 al-uppercase al-tracking-wider al-w-[150px]">
                    Applies To
                  </th>
                  <th className="al-px-4 al-py-3 al-w-[40px]"></th>
                </tr>
              </thead>
              <tbody className="al-bg-white">
                {Object.entries(policiesByDatabase[database]).flatMap(
                  ([table, policies], tableIndex) =>
                    (policies as Policy[]).map((policy, pIndex: number) => {
                      const rowKey = `${database}-${table}-${pIndex}`;
                      const isExpanded = expandedRows.has(rowKey);
                      const globalIndex =
                        Object.entries(policiesByDatabase[database])
                          .slice(0, tableIndex)
                          .reduce((acc, [, pols]) => acc + (pols as Policy[]).length, 0) +
                        pIndex +
                        1;

                      return (
                        <>
                          <tr
                            key={rowKey}
                            onClick={() => toggleRow(rowKey)}
                            className="al-hover:bg-gray-50 al-cursor-pointer al-border-b al-border-gray-200"
                          >
                            <td className="al-px-4 al-py-4 al-text-gray-500 al-text-sm">
                              {globalIndex}
                            </td>
                            <td className="al-px-4 al-py-4 al-font-medium al-text-gray-900">
                              {table}
                            </td>
                            <td className="al-px-4 al-py-4">
                              <Typography variant="body" className="al-text-sm">
                                {policy.policy_name}
                              </Typography>
                            </td>
                            <td className="al-px-4 al-py-4">
                              {policy.is_restrictive ? (
                                <Badge className="!al-bg-red-100 !al-text-red-700 al-border al-border-red-200">
                                  Restrictive
                                </Badge>
                              ) : (
                                <Badge className="!al-bg-green-100 !al-text-green-700 al-border al-border-green-200">
                                  Permissive
                                </Badge>
                              )}
                            </td>
                            <td className="al-px-4 al-py-4">
                              <Stack direction="row" gap="xs" className="al-flex-wrap">
                                {policy.apply_to_list.map((role, rIndex: number) => (
                                  <Badge
                                    key={rIndex}
                                    className="!al-bg-cyan-100 !al-text-cyan-700 al-border al-border-cyan-200 al-text-xs"
                                  >
                                    {role}
                                  </Badge>
                                ))}
                              </Stack>
                            </td>
                            <td className="al-px-4 al-py-4 al-text-gray-400">
                              {isExpanded ? (
                                <IconChevronDown className="al-w-4 al-h-4" />
                              ) : (
                                <IconChevronRight className="al-w-4 al-h-4" />
                              )}
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr
                              key={`${rowKey}-expanded`}
                              className="al-border-b al-border-gray-200"
                            >
                              <td colSpan={6} className="al-px-4 al-py-4 al-bg-gray-50">
                                <div>
                                  <Typography
                                    variant="body"
                                    weight="medium"
                                    className="al-text-gray-700 al-text-xs al-uppercase al-mb-2"
                                  >
                                    Filter Expression:
                                  </Typography>
                                  <div className="al-p-4 al-bg-white al-border al-border-gray-200 al-rounded-lg al-overflow-x-auto">
                                    <pre className="al-font-mono al-text-sm al-whitespace-pre-wrap al-text-gray-900 al-leading-relaxed">
                                      {formatFilterExpression(policy.select_filter)}
                                    </pre>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </>
                      );
                    })
                )}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
